import * as React from 'react';
import { useEffect, useState, useRef, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

/**
 * Props for the ProtectedRoute component
 */
interface ProtectedRouteProps {
  children: ReactNode;
}

/**
 * A component that protects routes by checking if the user is authenticated.
 * If not authenticated, it redirects to the sign in page.
 * Can be disabled with REACT_APP_DISABLE_AUTH environment variable.
 * 
 * @param {ProtectedRouteProps} props - Component props
 * @returns {JSX.Element} - The protected route component
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, signin, user } = useAuth();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  
  // Check if authentication is disabled via environment variable
  const isAuthDisabled = process.env.REACT_APP_DISABLE_AUTH === 'true';
  
  // Use refs to prevent multiple redirects
  const isRedirecting = useRef<boolean>(false);
  const hasStoredPath = useRef<boolean>(false);

  // Effect to check if user exists and update loading state
  useEffect(() => {
    // If authentication is disabled, skip auth checks
    if (isAuthDisabled) {
      console.log('Authentication disabled, bypassing auth checks');
      setIsLoading(false);
      return;
    }
    
    // If we have a user, we're authenticated
    if (user) {
      console.log('User exists, authenticated');
      setIsLoading(false);
      isRedirecting.current = false;
    }
  }, [user, isAuthDisabled]);

  // Immediately check authentication and redirect if needed
  useEffect(() => {
    const checkAuthAndRedirect = async (): Promise<void> => {
      // Skip if authentication is disabled
      if (isAuthDisabled) {
        setIsLoading(false);
        return;
      }
      
      // Skip if we're already redirecting or if we have a user
      if (isRedirecting.current || user) {
        return;
      }
      
      try {
        console.log('Checking authentication status...');
        // Check if the user is authenticated
        const authenticated = await isAuthenticated();
        
        if (!authenticated) {
          // Store the current path and search params for redirect after sign in
          if (!hasStoredPath.current) {
            console.log('Storing redirect path:', location.pathname + location.search);
            sessionStorage.setItem('auth_redirect_path', location.pathname + location.search);
            hasStoredPath.current = true;
          }
          
          // Set flag to prevent multiple redirects
          isRedirecting.current = true;
          
          // Initiate sign in process immediately
          console.log('Not authenticated, redirecting to sign in immediately');
          signin();
        } else {
          // User is authenticated, we can stop loading
          console.log('Authentication check complete, user is authenticated');
          setIsLoading(false);
        }
      } catch (error) {
        console.error('Error checking authentication:', error);

        // On error, redirect to sign in to be safe
        if (!isRedirecting.current) {
          isRedirecting.current = true;
          signin();
        }
      }
    };

    // Run the check immediately
    checkAuthAndRedirect();
  }, [isAuthenticated, signin, location, user, isAuthDisabled]);

  // If we're still loading or redirecting, show a loading indicator
  // Skip loading screen if auth is disabled
  if (isLoading && !isAuthDisabled) {
    return (
      <div className="loading-indicator" data-testid="loading-indicator">
        <div className="text-center">
          <div className="spinner" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p data-testid="loading-text">
            {isRedirecting.current ? "Redirecting to sign in..." : "Checking authentication..."}
          </p>
        </div>
      </div>
    );
  }

  // Direct access to protected route content when authenticated
  return <>{children}</>;
};

export default ProtectedRoute;
